import { selectRegexp } from '@ps20-frontend/selector';
import { convertVermittlernummerToString } from '@ps20-frontend/frontend-models';
import { Vermittlernummer } from '@ps20/bff-models';
import { IAppState } from '../localStore';
import { Vermittler } from '../reducer/vermittler.reducer';
import { selectIsAzamRole } from './zahlung.selector';
import { isMaklerbetreuerOrMaklerOrUvm } from './role.selector';

export const formattedVtnrs = (state: IAppState): Array<string> =>
  state.vermittler.vermittlernummern
    .map(vtnr => {
      return convertVermittlernummerToString(vtnr);
    })
    .sort((a, b) => a.localeCompare(b));

export const selectedVtnrAsString = (state: IAppState): string =>
  convertVermittlernummerToString(state.vermittler.selectedVermittlernummer.eigen);

export const selectedFremdVtnrAsString = (state: IAppState): string => {
  if (selectVtnrOverriden(state)) {
    return convertVermittlernummerToString(
      state.vermittler.selectedVermittlernummer.mappedFromActionCode,
    );
  }
  return convertVermittlernummerToString(state.vermittler.selectedVermittlernummer.fremd);
};

export const hasMoreThanFiftyRoles = (state: IAppState): boolean => {
  return state.vermittler.hasMoreThanFiftyRoles;
};

export const selectActiveVermittlerNummer = (vermittler: Vermittler): Vermittlernummer => {
  if (vermittler == null) {
    return null;
  }
  if (vermittler.selectedVermittlernummer?.mappedFromActionCode) {
    return vermittler.selectedVermittlernummer.mappedFromActionCode;
  }
  return vermittler.fremd
    ? vermittler.selectedVermittlernummer?.fremd
    : vermittler.selectedVermittlernummer?.eigen;
};

export const selectBsvtnr = (state: IAppState): string[] => state.vermittler.bsvtnrs;

export const selectVermittlerWithUniqueBnrbs = (state: IAppState): Vermittler => {
  const vermittler = state.vermittler;
  vermittler.bnrbs = [...new Set(vermittler.bnrbs)];
  return vermittler;
};

export const selectPatternForAllowedVtnr = (state: IAppState): string => {
  const bsvtnrs = selectBsvtnr(state);

  if (bsvtnrs.includes('*') || bsvtnrs.length === 0 || !selectIsAzamRole(state)) {
    return selectRegexp(state, 'VTNR');
  }

  let combinedPatternParts = '';

  for (const bsvtnr of bsvtnrs) {
    const modifiedBsvtnr = bsvtnr.replace('*', '');
    let patternPartBeginning = '';

    for (let i = 0; i < modifiedBsvtnr.length; i++) {
      if ([2, 5].includes(i)) {
        patternPartBeginning += String.raw`[\/]`;
      }
      patternPartBeginning += modifiedBsvtnr[i];
    }

    let patternPartEnd = '';
    const remainingChars = 9 - modifiedBsvtnr.length;

    if (remainingChars > 7) {
      patternPartEnd += String.raw`\d{${remainingChars - 7}}`;
      patternPartEnd += String.raw`[\/]\d{3}[\/]\d{4}`;
    } else if (remainingChars > 4) {
      patternPartEnd += String.raw`\d{${remainingChars - 4}}`;
      patternPartEnd += String.raw`[\/]\d{4}`;
    } else if (remainingChars === 4) {
      patternPartEnd += String.raw`[\/]\d{${remainingChars}}`;
    } else if (remainingChars > 0) {
      patternPartEnd += String.raw`\d{${remainingChars}}`;
    }

    patternPartBeginning += patternPartEnd;

    if (combinedPatternParts.length === 0) {
      combinedPatternParts += patternPartBeginning;
    } else {
      combinedPatternParts += '|' + patternPartBeginning;
    }
  }
  return combinedPatternParts;
};

export const selectMaklerInkasso = (state: IAppState): boolean => {
  return state.vermittler.isInkasso && isMaklerbetreuerOrMaklerOrUvm(state);
};

export const selectVtnrOverriden = (state: IAppState): boolean => {
  return Boolean(state.vermittler.selectedVermittlernummer.mappedFromActionCode);
};

export const FREMD_VTNR_AAWORLD_MAPPING = new Map([
  ['10/150/3100', '10/150/3131'],
  ['10/150/3102', '10/150/3131'],
  ['10/150/3103', '10/150/3131'],
  ['10/150/3104', '10/150/3131'],
  ['10/150/3105', '10/150/3131'],

  ['10/151/3101', '10/150/3131'],
  ['10/151/3102', '10/150/3131'],
  ['10/151/3103', '10/150/3131'],
  ['10/151/3104', '10/150/3131'],
  ['10/151/3106', '10/150/3131'],

  ['40/524/1003', '40/524/1002'],
  ['40/524/1001', '40/524/1002'],

  ['40/524/2201', '40/524/2202'],
  ['40/524/2204', '40/524/2202'],
]);
